import { useEffect, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import { mensagemAmigavel } from '@/lib/mensagemAmigavel';
import { garantirUsuario } from '@/lib/usuario';
import { guardarDestino } from '@/lib/destino';
import { reduzirImagem } from '@/lib/imagem';
import { useMinhaFoto } from '@/hooks/useMinhaFoto';
import { mascaraCEP, buscarEnderecoPorCep } from '@/lib/cep';
import { suportaAvisos, estadoPermissao, situacaoAvisos, type EstadoPermissao } from '@/lib/avisos';
import { buscarMinhaTribo, abrirFolhaTribo, COR_TRIBO_PADRAO, type MinhaTribo } from '@/lib/tribo';
import {
  dataParaISO,
  mascaraCPF,
  mascaraData,
  mascaraTelefone,
  validarCPF,
} from '@/pages/inscricao/utils';
import CampoTexto from '@/components/base/CampoTexto';
import AvisosCelular from '@/components/feature/AvisosCelular';
import WhatsappPerfil from './components/WhatsappPerfil';
import FolhaAvisosBloqueados from './components/FolhaAvisosBloqueados';
import AvatarPessoa from '@/components/ui/AvatarPessoa';
import AppScreen from '@/components/ui/AppScreen';
import Card from '@/components/ui/Card';
import Accordion from '@/components/ui/Accordion';
import PrimaryButton from '@/components/ui/PrimaryButton';
import SecondaryButton from '@/components/ui/SecondaryButton';
import BlocoDados from './components/BlocoDados';
import CampoLeitura from './components/CampoLeitura';
import CartaoHistorico from './components/CartaoHistorico';
import { UFS } from './types';
import type { MeuPerfil, HistoricoItem, BlocoNome, RascunhoPerfil } from './types';

const RASCUNHO_VAZIO: RascunhoPerfil = {
  nome: '',
  cpf: '',
  nascimento: '',
  telefone: '',
  cep: '',
  endereco: '',
  complemento: '',
  bairro: '',
  cidade: '',
  uf: '',
};

function rascunhoDe(perfil: MeuPerfil | null, nomeFallback: string): RascunhoPerfil {
  return {
    nome: perfil?.nome || nomeFallback,
    cpf: perfil?.cpf ? mascaraCPF(perfil.cpf) : '',
    nascimento: formatarData(perfil?.nascimento ?? null),
    telefone: perfil?.telefone ? mascaraTelefone(perfil.telefone) : '',
    cep: perfil?.cep ? mascaraCEP(perfil.cep) : '',
    endereco: perfil?.endereco || '',
    complemento: perfil?.complemento || '',
    bairro: perfil?.bairro || '',
    cidade: perfil?.cidade || '',
    uf: perfil?.uf || '',
  };
}

function formatarData(iso: string | null): string {
  if (!iso) return '';
  const [ano, mes, dia] = iso.slice(0, 10).split('-');
  if (!dia || !mes || !ano) return iso;
  return `${dia}/${mes}/${ano}`;
}

// Linha de atalho: 56 px, ícone 20 px, texto e seta.
function Atalho({
  icone,
  titulo,
  valor,
  cor,
  para,
  onClick,
}: {
  icone: string;
  titulo: string;
  valor?: string;
  cor?: string | null;
  para?: string;
  onClick?: () => void;
}) {
  const conteudo = (
    <>
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-[12px] bg-background-100 text-foreground-700">
        {cor ? (
          <span className="h-3.5 w-3.5 rounded-full" style={{ backgroundColor: cor }} aria-hidden />
        ) : (
          <i className={`${icone} text-[20px]`} aria-hidden />
        )}
      </span>
      <span className="min-w-0 flex-1 text-[15px] font-semibold text-foreground-950">{titulo}</span>
      {valor && (
        <span className="max-w-[52%] shrink-0 text-right text-[12.5px] font-medium leading-tight text-foreground-500">
          {valor}
        </span>
      )}
      <span className="flex h-5 w-5 shrink-0 items-center justify-center text-foreground-400">
        <i className="ri-arrow-right-s-line text-xl" aria-hidden />
      </span>
    </>
  );
  const base = 'aperta flex min-h-[56px] w-full items-center gap-3 px-4 text-left';
  if (para) {
    return (
      <Link to={para} className={`${base} cursor-pointer`}>
        {conteudo}
      </Link>
    );
  }
  return (
    <button type="button" onClick={onClick} className={`${base} cursor-pointer`}>
      {conteudo}
    </button>
  );
}

export default function MeuPerfilPage() {
  const navigate = useNavigate();
  const { user, pronto } = useAuth();

  const [uid, setUid] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState<string | null>(null);
  const [reload, setReload] = useState(0);

  const [perfil, setPerfil] = useState<MeuPerfil | null>(null);
  const [nomeFallback, setNomeFallback] = useState('');
  const [historico, setHistorico] = useState<HistoricoItem[]>([]);
  const [organiza, setOrganiza] = useState(false);

  const [avisosAtivos, setAvisosAtivos] = useState(false);
  const [permissao, setPermissao] = useState<EstadoPermissao>('unsupported');
  const [tribo, setTribo] = useState<MinhaTribo | null>(null);

  const [secao, setSecao] = useState<'nenhuma' | 'dados' | 'avisos'>('nenhuma');
  const [avisosBloqueadosAberto, setAvisosBloqueadosAberto] = useState(false);

  const { definirFoto, recarregar } = useMinhaFoto();
  const [trocandoFoto, setTrocandoFoto] = useState(false);
  const [erroFoto, setErroFoto] = useState<string | null>(null);
  const fotoInputRef = useRef<HTMLInputElement>(null);

  const [editando, setEditando] = useState<BlocoNome | null>(null);
  const [rascunho, setRascunho] = useState<RascunhoPerfil>(RASCUNHO_VAZIO);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [erroGeral, setErroGeral] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [salvo, setSalvo] = useState<BlocoNome | null>(null);
  const timerSalvo = useRef<number | null>(null);

  useEffect(() => {
    if (!pronto) return;
    if (!user) {
      guardarDestino();
      navigate(`/entrar?voltar=${encodeURIComponent('/meu-perfil')}`, { replace: true });
      return;
    }
    let ativo = true;

    async function carregar() {
      setLoading(true);
      setErro(null);
      try {
        if (ativo) setUid(user.id);
        await garantirUsuario();

        const { data: perfilData } = await supabase
          .from('vw_meu_perfil')
          .select('*')
          .maybeSingle();

        const { data: histData, error: errHist } = await supabase.rpc('meu_historico');
        if (errHist) throw errHist;

        const { data: evs } = await supabase.rpc('meus_eventos');

        if (ativo) {
          if (perfilData) {
            const p = perfilData as MeuPerfil;
            setPerfil(p);
            setNomeFallback(p.nome);
          } else {
            const { data: usu } = await supabase
              .from('usuarios')
              .select('nome')
              .eq('id', user.id)
              .maybeSingle();
            const nome =
              usu?.nome ||
              (user.user_metadata?.full_name as string | undefined) ||
              (user.user_metadata?.name as string | undefined) ||
              '';
            setPerfil(null);
            setNomeFallback(nome || '');
          }
          setHistorico((histData as HistoricoItem[]) || []);
          setOrganiza(((evs as unknown[]) || []).length > 0);
        }
      } catch (e) {
        if (ativo) {
          setErro(mensagemAmigavel(e, 'Não foi possível carregar seu perfil. Tente de novo.'));
        }
      } finally {
        if (ativo) setLoading(false);
      }
    }

    carregar();
    return () => {
      ativo = false;
    };
  }, [navigate, reload, pronto, user]);

  // Estado dos avisos no celular (para o rótulo do atalho).
  useEffect(() => {
    let vivo = true;
    (async () => {
      const perm = estadoPermissao();
      const sit = await situacaoAvisos();
      if (!vivo) return;
      setPermissao(perm);
      setAvisosAtivos(perm === 'granted' && sit.ativa);
    })();
    return () => {
      vivo = false;
    };
  }, [secao]);

  // Minha tribo (linha só aparece para quem é da equipe ou já respondeu).
  useEffect(() => {
    let vivo = true;
    buscarMinhaTribo()
      .then((d) => {
        if (vivo) setTribo(d);
      })
      .catch(() => {
        /* sem linha se não der para saber */
      });
    return () => {
      vivo = false;
    };
  }, []);

  useEffect(() => {
    return () => {
      if (timerSalvo.current) window.clearTimeout(timerSalvo.current);
    };
  }, []);

  // Busca o endereço pelo CEP enquanto a pessoa digita.
  useEffect(() => {
    if (editando !== 'onde') return;
    if (rascunho.cep.replace(/\D/g, '').length !== 8) return;

    let ativo = true;
    buscarEnderecoPorCep(rascunho.cep).then((end) => {
      if (!ativo || !end) return;
      setRascunho((r) => ({
        ...r,
        endereco: r.endereco || end.logradouro,
        bairro: r.bairro || end.bairro,
        cidade: r.cidade || end.localidade,
        uf: r.uf || end.uf,
      }));
    });
    return () => {
      ativo = false;
    };
  }, [rascunho.cep, editando]);

  const nomeExibido = perfil?.nome || nomeFallback;
  const telefoneBonito = perfil?.telefone ? mascaraTelefone(perfil.telefone) : '';
  const semPerfil = perfil === null;
  const incompleto = perfil?.cadastro_incompleto === true;

  // Ao abrir o perfil, relê a foto do banco (ex.: vinda de uma inscrição nova).
  useEffect(() => {
    recarregar();
  }, [recarregar]);

  function editar(bloco: BlocoNome) {
    setRascunho(rascunhoDe(perfil, nomeFallback));
    setErros({});
    setErroGeral(null);
    setEditando(bloco);
  }

  function cancelar() {
    setEditando(null);
    setErros({});
    setErroGeral(null);
  }

  function mudar(campo: keyof RascunhoPerfil, valor: string) {
    let v = valor;
    if (campo === 'cpf') v = mascaraCPF(valor);
    if (campo === 'telefone') v = mascaraTelefone(valor);
    if (campo === 'cep') v = mascaraCEP(valor);
    if (campo === 'nascimento') v = mascaraData(valor);
    setRascunho((r) => ({ ...r, [campo]: v }));
    setErros((e) => ({ ...e, [campo]: '' }));
  }

  function rolarParaErro() {
    requestAnimationFrame(() => {
      const el = document.querySelector('[data-erro="true"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  async function salvar() {
    if (!editando) return;
    const bloco = editando;
    const ehQuem = bloco === 'quem';

    const novosErros: Record<string, string> = {};
    if (ehQuem) {
      if (!rascunho.nome.trim()) novosErros.nome = 'Digite seu nome completo.';
      if (!rascunho.cpf) novosErros.cpf = 'Digite seu CPF.';
      else if (!validarCPF(rascunho.cpf)) novosErros.cpf = 'Verifique o CPF digitado.';
      if (!rascunho.nascimento) novosErros.nascimento = 'Digite sua data de nascimento.';
      else if (rascunho.nascimento.replace(/\D/g, '').length !== 8)
        novosErros.nascimento = 'Digite a data completa, como 13/09/2000.';
      if (!rascunho.telefone.replace(/\D/g, '')) novosErros.telefone = 'Digite seu telefone, com DDD.';
    } else {
      if (!rascunho.cep.replace(/\D/g, '')) novosErros.cep = 'Digite seu CEP.';
      if (!rascunho.endereco.trim()) novosErros.endereco = 'Digite seu endereço.';
      if (!rascunho.bairro.trim()) novosErros.bairro = 'Digite seu bairro.';
      if (!rascunho.cidade.trim()) novosErros.cidade = 'Digite sua cidade.';
      if (!rascunho.uf) novosErros.uf = 'Escolha seu estado.';
    }

    setErros(novosErros);
    setErroGeral(null);
    if (Object.keys(novosErros).length > 0) {
      rolarParaErro();
      return;
    }

    setSalvando(true);
    try {
      const args = ehQuem
        ? {
            p_nome: rascunho.nome.trim(),
            p_cpf: rascunho.cpf || null,
            p_nascimento: rascunho.nascimento ? dataParaISO(rascunho.nascimento) : null,
            p_telefone: rascunho.telefone || null,
          }
        : {
            p_nome: nomeExibido.trim() || rascunho.nome.trim(),
            p_cep: rascunho.cep || null,
            p_endereco: rascunho.endereco || null,
            p_complemento: rascunho.complemento || null,
            p_bairro: rascunho.bairro || null,
            p_cidade: rascunho.cidade || null,
            p_uf: rascunho.uf || null,
          };

      const { error: errRpc } = await supabase.rpc('salvar_meu_perfil', args);
      if (errRpc) {
        setErroGeral(mensagemAmigavel(errRpc));
        return;
      }

      const { data: perfilData } = await supabase
        .from('vw_meu_perfil')
        .select('*')
        .maybeSingle();
      if (perfilData) {
        const p = perfilData as MeuPerfil;
        setPerfil(p);
        setNomeFallback(p.nome);
      }

      setEditando(null);
      setErros({});
      setSalvo(bloco);
      if (timerSalvo.current) window.clearTimeout(timerSalvo.current);
      timerSalvo.current = window.setTimeout(() => setSalvo(null), 2000);
    } catch (e) {
      setErroGeral(mensagemAmigavel(e, 'Não foi possível salvar. Tente de novo.'));
    } finally {
      setSalvando(false);
    }
  }

  async function trocarFoto(arquivo: File) {
    if (!uid) return;
    setTrocandoFoto(true);
    setErroFoto(null);
    try {
      const reduzido = await reduzirImagem(arquivo);
      if (reduzido.size > 5 * 1024 * 1024) {
        setErroFoto('Esta imagem é muito grande. Tente outra foto.');
        return;
      }

      const caminho = `${uid}/perfil-${crypto.randomUUID()}.jpg`;
      const { error: errUpload } = await supabase.storage
        .from('fichas')
        .upload(caminho, reduzido, { contentType: 'image/jpeg' });
      if (errUpload) {
        setErroFoto('Não conseguimos enviar a foto. Verifique sua internet e tente de novo.');
        return;
      }

      const { error: errRpc } = await supabase.rpc('salvar_meu_perfil', {
        p_nome: nomeExibido.trim(),
        p_foto_url: caminho,
      });
      if (errRpc) {
        setErroFoto(mensagemAmigavel(errRpc));
        return;
      }

      definirFoto(caminho);
      if (perfil) setPerfil({ ...perfil, foto_url: caminho });
    } catch (e) {
      setErroFoto(mensagemAmigavel(e, 'Não conseguimos enviar a foto. Tente de novo.'));
    } finally {
      setTrocandoFoto(false);
    }
  }

  function aoEscolherFoto(e: React.ChangeEvent<HTMLInputElement>) {
    const arquivo = e.target.files?.[0];
    if (arquivo) trocarFoto(arquivo);
    e.target.value = '';
  }

  async function sairDeVez() {
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } finally {
      navigate('/');
    }
  }

  if (loading) {
    return (
      <AppScreen>
        <div className="mx-auto w-full max-w-[560px] px-5 py-8">
          <div className="flex items-center gap-4">
            <div className="h-16 w-16 animate-pulse rounded-full bg-background-200/70" />
            <div className="space-y-2">
              <div className="h-5 w-40 animate-pulse rounded bg-background-200/70" />
              <div className="h-4 w-28 animate-pulse rounded bg-background-200/70" />
            </div>
          </div>
          <div className="mt-6 h-64 animate-pulse rounded-[18px] bg-background-200/70" />
        </div>
      </AppScreen>
    );
  }

  if (erro) {
    return (
      <AppScreen>
        <div className="mx-auto w-full max-w-[420px] px-5 py-10">
          <Card className="p-8 text-center">
            <span className="mx-auto flex h-14 w-14 items-center justify-center text-foreground-300">
              <i className="ri-error-warning-line text-[40px]" aria-hidden />
            </span>
            <h1 className="mt-4 text-[20px] font-extrabold text-foreground-950">
              Não foi possível carregar
            </h1>
            <p className="mt-2 text-[14px] text-foreground-600">{erro}</p>
            <PrimaryButton cheio className="mt-6" onClick={() => setReload((r) => r + 1)}>
              Tentar de novo
            </PrimaryButton>
          </Card>
        </div>
      </AppScreen>
    );
  }

  const editandoQuem = editando === 'quem';
  const editandoOnde = editando === 'onde';

  const rotuloAvisos = !suportaAvisos()
    ? 'Indisponível'
    : avisosAtivos
      ? 'Ligados'
      : permissao === 'denied'
        ? 'Bloqueados — toque para ver como liberar'
        : 'Desligados';

  function abrirAvisos() {
    if (permissao === 'denied') {
      setAvisosBloqueadosAberto(true);
      return;
    }
    setSecao((s) => (s === 'avisos' ? 'nenhuma' : 'avisos'));
  }

  const mostraTribo = !!tribo && (tribo.tribo != null || tribo.equipe === true);
  const valorTribo = tribo?.tribo?.nome ?? (tribo?.resposta === 'nao_campista' ? 'Não fui campista' : '');

  return (
    <AppScreen>
      <main className="mx-auto w-full max-w-[560px] px-5 pt-8">
        {/* Cabeçalho: avatar + nome + telefone */}
        <div className="app-entra flex items-center gap-4">
          <div className="relative">
            <AvatarPessoa tamanho={64} textoClassName="text-[22px]" />
            <button
              type="button"
              onClick={() => fotoInputRef.current?.click()}
              disabled={trocandoFoto}
              aria-label="Trocar foto"
              className="aperta absolute -bottom-1 -right-1 flex h-7 w-7 cursor-pointer items-center justify-center rounded-full border-2 border-background-100 bg-background-50 text-foreground-700 shadow-pcard disabled:opacity-60"
            >
              <i className={trocandoFoto ? 'ri-loader-4-line animate-spin text-[13px]' : 'ri-camera-line text-[13px]'} aria-hidden />
            </button>
            <input
              ref={fotoInputRef}
              type="file"
              accept="image/*"
              capture="user"
              className="hidden"
              onChange={aoEscolherFoto}
            />
          </div>
          <div className="min-w-0 flex-1">
            <h1 className="truncate text-[22px] font-extrabold text-foreground-950">
              {nomeExibido || 'Seu perfil'}
            </h1>
            <p className="mt-0.5 truncate text-[14px] text-foreground-500">
              {telefoneBonito || 'Complete seu cadastro'}
            </p>
          </div>
        </div>

        {(semPerfil || incompleto) && (
          <div className="app-entra mt-4 flex items-start gap-3 rounded-[18px] bg-warning-50 px-4 py-3">
            <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center text-warning-600">
              <i className="ri-information-line" aria-hidden />
            </span>
            <p className="text-[14px] leading-relaxed text-warning-800">
              {semPerfil
                ? 'Complete seu cadastro para agilizar suas próximas inscrições.'
                : 'Faltam alguns dados. Preencher agora deixa suas próximas inscrições em dois cliques.'}
            </p>
          </div>
        )}

        {erroFoto && (
          <div className="mt-4 rounded-[18px] bg-danger-50 px-4 py-3 text-[14px] text-danger-700">
            {erroFoto}
          </div>
        )}

        {/* Atalhos */}
        <Card className="app-entra mt-6 overflow-hidden">
          <div className="divide-y divide-background-200/70">
            <Atalho
              icone="ri-user-3-line"
              titulo="Meus dados"
              onClick={() => setSecao((s) => (s === 'dados' ? 'nenhuma' : 'dados'))}
            />
            <Atalho
              icone="ri-notification-3-line"
              titulo="Avisos no celular"
              valor={rotuloAvisos}
              onClick={abrirAvisos}
            />
            {mostraTribo &&
              (tribo?.tribo ? (
                <Atalho
                  icone="ri-tent-line"
                  cor={tribo.tribo.cor ?? COR_TRIBO_PADRAO}
                  titulo="Minha tribo"
                  valor={tribo.tribo.nome}
                  para={`/tribo/${tribo.tribo.id}`}
                />
              ) : (
                <Atalho
                  icone="ri-tent-line"
                  titulo="Minha tribo"
                  valor={valorTribo || 'Responder'}
                  onClick={() => abrirFolhaTribo()}
                />
              ))}
            <Atalho
              icone="ri-ticket-2-line"
              titulo="Meus ingressos e fichas"
              para="/meus-ingressos"
            />
            {organiza && (
              <Atalho icone="ri-dashboard-line" titulo="Painel do organizador" para="/meus-eventos" />
            )}
            <Atalho icone="ri-question-answer-line" titulo="Ajuda" para="/conheca" />
          </div>
        </Card>

        {/* Meus dados */}
        {secao === 'dados' && (
          <div className="app-entra mt-4 space-y-4">
            <BlocoDados
              titulo="Quem sou eu"
              editando={editandoQuem}
              salvando={salvando && editandoQuem}
              salvo={salvo === 'quem'}
              onEditar={() => editar('quem')}
              onCancelar={cancelar}
              onSalvar={salvar}
            >
              {editandoQuem ? (
                <>
                  <CampoTexto
                    id="nome"
                    name="nome"
                    label="Nome completo"
                    value={rascunho.nome}
                    onChange={(v) => mudar('nome', v)}
                    required
                    error={erros.nome}
                    autoComplete="name"
                  />
                  <CampoTexto
                    id="cpf"
                    name="cpf"
                    label="CPF"
                    value={rascunho.cpf}
                    onChange={(v) => mudar('cpf', v)}
                    required
                    error={erros.cpf}
                    inputMode="numeric"
                    placeholder="000.000.000-00"
                    autoComplete="off"
                  />
                  <CampoTexto
                    id="nascimento"
                    name="nascimento"
                    label="Data de nascimento"
                    value={rascunho.nascimento}
                    onChange={(v) => mudar('nascimento', v)}
                    required
                    error={erros.nascimento}
                    inputMode="numeric"
                    placeholder="DD/MM/AAAA"
                    autoComplete="bday"
                  />
                  <CampoTexto
                    id="telefone"
                    name="telefone"
                    label="Telefone"
                    help="Com DDD. É por aqui que a organização fala com você."
                    value={rascunho.telefone}
                    onChange={(v) => mudar('telefone', v)}
                    required
                    error={erros.telefone}
                    inputMode="tel"
                    placeholder="(00) 00000-0000"
                    autoComplete="tel"
                  />
                </>
              ) : (
                <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                  <CampoLeitura rotulo="Nome completo" obrigatorio valor={perfil?.nome || ''} />
                  <CampoLeitura
                    rotulo="CPF"
                    obrigatorio
                    valor={perfil?.cpf ? mascaraCPF(perfil.cpf) : ''}
                  />
                  <CampoLeitura
                    rotulo="Data de nascimento"
                    obrigatorio
                    valor={formatarData(perfil?.nascimento ?? null)}
                  />
                  <CampoLeitura
                    rotulo="Telefone"
                    obrigatorio
                    valor={perfil?.telefone ? mascaraTelefone(perfil.telefone) : ''}
                  />
                </div>
              )}
            </BlocoDados>

            <BlocoDados
              titulo="Onde eu moro"
              editando={editandoOnde}
              salvando={salvando && editandoOnde}
              salvo={salvo === 'onde'}
              onEditar={() => editar('onde')}
              onCancelar={cancelar}
              onSalvar={salvar}
            >
              {editandoOnde ? (
                <>
                  <CampoTexto
                    id="cep"
                    name="cep"
                    label="CEP"
                    help="Ao digitar, buscamos o endereço sozinhos."
                    value={rascunho.cep}
                    onChange={(v) => mudar('cep', v)}
                    required
                    error={erros.cep}
                    inputMode="numeric"
                    placeholder="00000-000"
                    autoComplete="postal-code"
                  />
                  <CampoTexto
                    id="endereco"
                    name="endereco"
                    label="Endereço"
                    value={rascunho.endereco}
                    onChange={(v) => mudar('endereco', v)}
                    required
                    error={erros.endereco}
                    autoComplete="address-line1"
                  />
                  <CampoTexto
                    id="complemento"
                    name="complemento"
                    label="Complemento"
                    value={rascunho.complemento}
                    onChange={(v) => mudar('complemento', v)}
                    error={erros.complemento}
                    autoComplete="address-line2"
                  />
                  <div className="grid grid-cols-1 gap-x-6 sm:grid-cols-2">
                    <CampoTexto
                      id="bairro"
                      name="bairro"
                      label="Bairro"
                      value={rascunho.bairro}
                      onChange={(v) => mudar('bairro', v)}
                      required
                      error={erros.bairro}
                    />
                    <CampoTexto
                      id="cidade"
                      name="cidade"
                      label="Cidade"
                      value={rascunho.cidade}
                      onChange={(v) => mudar('cidade', v)}
                      required
                      error={erros.cidade}
                    />
                  </div>
                  <div>
                    <label htmlFor="uf" className="block text-[14px] font-semibold text-foreground-800">
                      Estado <span className="text-danger-600">*</span>
                    </label>
                    <select
                      id="uf"
                      name="uf"
                      value={rascunho.uf}
                      onChange={(e) => mudar('uf', e.target.value)}
                      className={`mt-1.5 w-full rounded-[14px] border bg-background-50 px-4 py-3 text-[15px] text-foreground-950 outline-none transition-colors focus:border-primary-500 ${
                        erros.uf ? 'border-danger-500' : 'border-background-200'
                      }`}
                    >
                      <option value="">Selecione</option>
                      {UFS.map((uf) => (
                        <option key={uf} value={uf}>
                          {uf}
                        </option>
                      ))}
                    </select>
                    {erros.uf && (
                      <p className="mt-1 text-[13px] text-danger-600" role="alert" data-erro="true">
                        {erros.uf}
                      </p>
                    )}
                  </div>
                </>
              ) : (
                <div className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2">
                  <CampoLeitura rotulo="CEP" obrigatorio valor={perfil?.cep ? mascaraCEP(perfil.cep) : ''} />
                  <CampoLeitura rotulo="Endereço" obrigatorio valor={perfil?.endereco || ''} />
                  <CampoLeitura rotulo="Complemento" valor={perfil?.complemento || ''} />
                  <CampoLeitura rotulo="Bairro" obrigatorio valor={perfil?.bairro || ''} />
                  <CampoLeitura rotulo="Cidade" obrigatorio valor={perfil?.cidade || ''} />
                  <CampoLeitura rotulo="Estado" obrigatorio valor={perfil?.uf || ''} />
                </div>
              )}
            </BlocoDados>

            {erroGeral && (
              <div className="rounded-[18px] bg-danger-50 px-4 py-3 text-[14px] text-danger-700">
                {erroGeral}
              </div>
            )}
          </div>
        )}

        {/* Avisos */}
        {secao === 'avisos' && (
          <div className="app-entra mt-4 space-y-3">
            <AvisosCelular />
            <WhatsappPerfil />
          </div>
        )}

        {/* Minha caminhada */}
        <div className="app-entra mt-6">
          <Accordion titulo="Minha caminhada" resumo={`${historico.length}`}>
            <CartaoHistorico itens={historico} />
          </Accordion>
        </div>

        {/* Sua conta */}
        <div className="app-entra mt-3">
          <Accordion titulo="Privacidade e conta">
            <div className="space-y-3">
              <Link
                to="/privacidade"
                className="flex min-h-[48px] cursor-pointer items-center gap-3 text-[15px] font-medium text-foreground-800"
              >
                <i className="ri-shield-user-line text-[20px] text-foreground-500" aria-hidden />
                Política de privacidade
              </Link>
              <Link
                to="/termos"
                className="flex min-h-[48px] cursor-pointer items-center gap-3 text-[15px] font-medium text-foreground-800"
              >
                <i className="ri-file-list-3-line text-[20px] text-foreground-500" aria-hidden />
                Termos de uso
              </Link>
              <SecondaryButton
                cheio
                icone="ri-delete-bin-6-line"
                onClick={() => navigate('/excluir-conta')}
              >
                Excluir minha conta
              </SecondaryButton>
            </div>
          </Accordion>
        </div>

        {/* Sair — discreto, sem caixa */}
        <button
          type="button"
          onClick={sairDeVez}
          className="aperta mx-auto mt-6 block cursor-pointer whitespace-nowrap py-3 text-[15px] font-semibold text-danger-600"
        >
          Sair deste celular
        </button>

        <div className="h-4" />
      </main>

      <FolhaAvisosBloqueados
        aberto={avisosBloqueadosAberto}
        aoFechar={() => {
          setAvisosBloqueadosAberto(false);
          setPermissao(estadoPermissao());
        }}
      />
    </AppScreen>
  );
}